import { USER_ACTIVE_STATUS_API_ROUTE } from '~/routes/remix-api.user.active-status';

/** The active status of a user. */
export type UserActiveStatus = 'active' | 'inactive';

/**
 * Map of member ID to status, as returned by the active-status endpoint.
 * `null` means the member is not a contact of the requesting user.
 */
export type UserActiveStatusMap = Record<number, UserActiveStatus | null>;

interface ActiveStatusResponse {
    statuses?: UserActiveStatusMap | null;
}

/**
 * Fetch active statuses for a batch of member IDs. Uses a plain `fetch` (not a
 * Remix fetcher) so every failure mode — non-2xx, transport error, abort, or
 * unparseable body — collapses to a single `null` the caller can back off on.
 *
 * @param memberIDs member IDs to look up (must already be batched/bounded)
 * @param signal    optional abort signal, used to cancel on logout/unmount
 */
export async function fetchUserActiveStatuses(
    memberIDs: number[],
    signal?: AbortSignal,
): Promise<UserActiveStatusMap | null> {
    if (memberIDs.length === 0) {
        return {};
    }
    const params = new URLSearchParams();
    for (const id of memberIDs) {
        params.append('memberIDs', String(id));
    }
    try {
        const response = await fetch(
            `${USER_ACTIVE_STATUS_API_ROUTE}?${params.toString()}`,
            { signal, headers: { Accept: 'application/json' } },
        );
        if (!response.ok) {
            return null;
        }
        const body = (await response.json()) as ActiveStatusResponse | null;
        return body?.statuses ?? null;
    } catch {
        return null;
    }
}
